export const BoardTypeValues = [
  "LESSON",
  "QUESTION",
  "ANSWER",
  "PRACTICE",
] as const;

export type BoardType =
  (typeof BoardTypeValues)[number];

export type BusEventType =
  | "EV_ALT_CLICK"
  | "EV_QUESTION_SAVED"
  | "EV_COPY"
  | "EV_PASTE"
  | "EV_PRACTICE_PROBLEM_PASTE"
  | "EV_SHORTCUT_KEYUP"
  | "EV_KEYUP"
  | "EV_KEYDOWN"
  | "EV_POINTERUP"
  | "EV_SVG_POINTERUP"
  | "EV_SVG_POINTERCANCEL"
  | "EV_SVG_POINTERDOWN"
  | "EV_SVG_POINTERMOVE"
  | "EV_AREA_SELECTED"
  | "EV_AREA_SELECTION_DONE"
  | "EV_CURVE_SELECTED"
  | "EV_CIRCLE_SELECTED"
  | "EV_CONIC_SELECTED"
  | "EV_IMAGE_SELECTED"
  | "EV_TEXT_SELECTED"
  | "EV_TEXT_EDITING"
  | "EV_ANNOTATION_SELECTED"
  | "EV_SPECIAL_SYMBOL_SELECTED"
  | "EV_SQRT_SELECTED"
  | "EV_LINE_CHANGED"
  | "EV_DIVISIONLINE_SELECTED"
  | "EV_LINE_SELECTED"
  | "EV_NOTATION_SELECTED"
  | "EV_TEXT_SYNC"
  | "EV_FREE_SKETCH_SELECTED"
  | "EV_MOBILE_ESCAPE";

export const NotationTypeValues = [
  "SYMBOL",
  "LOGBASE",
  "EXPONENT",
  "POLYGON",
  "LINE",
  "DIVISIONLINE",
  "CURVE",
  "CIRCLE",
  "CONIC",
  "SQRT",
  "SQRTSYMBOL",
  "TEXT",
  "ANNOTATION",
  "IMAGE",
  "FREESKETCH",
] as const;

export type NotationType =
  (typeof NotationTypeValues)[number];

export type CursorType =
  | "auto"
  | "crosshair"
  | "default"
  | "grab"
  | "grabbing"
  | "help"
  | "move"
  | "nesw-resize"
  | "none"
  | "not-allowed"
  | "nwse-resize"
  | "pointer"
  | "progress"
  | "text"
  | "wait";

export type GlobalEditMode =
  | "TEXT"
  | "FREE_SKETCH"
  | "FREE_SKETCH_WITH_OCR"
  | "LINE"
  | "ANNOTATION";

export type EditMode =
  | "LESSONS_SELECTION" //  after click on lessons icon
  | "QUESTIONS_SELECTION" //  after click on questions icon
  | "ANSWERS_SELECTION" //  after click on answers icon
  | "SPECIAL_SYMBOL_SELECTED" // special symbol in bottom toolbar pressed
  | "LOG_STARTED" // log button pressed
  | "EXPONENT_STARTED" // exponent button pressed
  | "EXPONENT_WRITING" // user clicked a cell following exponent button pressed
  | "EXPONENT_SELECTED" // user clicked on existing exponent
  | "AREA_SELECTION_STARTED" // user started selecting arrea in mobile
  | "TEXT_STARTED" // text button pressed
  | "TEXT_SELECTED" // user clicked on existing text rectangle
  | "TEXT_AREA_SELECTING" // user started selecting area following text button pressed
  | "TEXT_WRITING" // user clicked a cell following text button pressed
  | "IMAGE_SELECTED" // user clicked on existing image
  | "IMAGE_CROPPING" // user is cropping a selected image
  | "RESIZE_STARTED" // user clicked on existing rect and clicked on resize handle
  | "RESIZING" // user clicked on existing rect  and is resizing
  | "ANNOTATION_STARTED" // annotation button pressed
  | "ANNOTATION_SELECTED" // user clicked on existing annotation rectangle
  | "ANNOTATION_WRITING" // user clicked a cell following annotation button pressed
  | "CELL_SELECTED" // user clicked on a cell or navigated via keys
  | "POLYGON_STARTED" // polygon button pressed
  | "POLYGON_DRAWING" // polygon drawing started
  | "LINE_STARTED" //  line button pressed
  | "LINE_DRAWING" // line drawing started
  | "LINE_SELECTED" // line selected
  | "LINE_EDITING_LEFT" // slope line handle clicked followin slope line selection
  | "LINE_EDITING_RIGHT" // slope line handle clicked followin slope line selection
  | "DIVISIONLINE_STARTED" // division line button pressed
  | "DIVISIONLINE_DRAWING" // division line drawing started
  | "DIVISIONLINE_SELECTED" // division line selected
  | "DIVISIONLINE_EDITING_LEFT" // division line left handle clicked following division line selection
  | "DIVISIONLINE_EDITING_RIGHT" // division line right handle clicked following division line selection
  | "CURVE_STARTED" // curve button pressed
  | "CURVE_DRAWING" // curve drawing started
  | "CURVE_SELECTED" // curve selected
  | "CURVE_EDITING_LEFT" //   curve left handle clicked
  | "CURVE_EDITING_RIGHT" //  curve right handle clicked
  | "CURVE_EDITING_CONTROLֹ_POINT" //  curve control point clicked
  | "CIRCLE_STARTED" // circle button pressed
  | "CIRCLE_DRAWING" // circle drawing started
  | "CIRCLE_SELECTED" // circle selected
  | "CIRCLE_EDITING" // circle left handle clicked
  | "PARABOLA_STARTED" // parabola button pressed
  | "HYPERBOLA_STARTED" // hyperbola button pressed
  | "CONIC_DRAWING" // conic drawing started
  | "CONIC_SELECTED" // conic selected
  | "CONIC_EDITING_VERTEX" // vertex/center handle — move the whole shape
  | "CONIC_EDITING_SCALE" // size handle (parabola width / hyperbola a)
  | "CONIC_EDITING_OPENING" // shape handle (parabola curvature / hyperbola b)
  | "CONIC_EDITING_THROUGH" // drag a numbered through-point to refit a parabola
  | "SQRT_STARTED" // sqrt button pressed
  | "SQRT_DRAWING" // sqrt drawing started
  | "SQRT_EDITING" // sqrt line handle clicked
  | "SQRT_SELECTED" // sqrt selected
  | "FREE_SKETCH_STARTED" // free sketch button pressed
  | "FREE_SKETCH_DRAWING" // free sketch drawing started
  | "FREE_SKETCH_SELECTED" // free sketch selected
  | "FREE_SKETCH_WITH_OCR_STARTED" // sketch-to-symbol OCR mode
  | "FREE_SKETCH_WITH_OCR_DRAWING" // stroke in OCR sketch mode
  | "COLORIZING" // color selected
  | "DELETING" // mouse clicked following delete button pressed
  | "AREA_SELECTED" // user finished selecting area
  | "TEXT_AREA_SELECTING" // user started selecting area after click on free text icon
  | "TEXT_AREA_SELECTED" // user finished selecting text area
  | "AREA_MOVING" // user grabbed the selection area after select button pressed
  | "CHECKMARK_STARTED" // checkmark button pressed
  | "SEMICHECKMARK_STARTED" // semicheck button pressed
  | "XMARK_STARTED" // xmark button pressed
  | "STUDENTS_MONITORING" // teacher is monitoring students' boards
  | "CARTESIAN_SYSTEM_STARTED"; // cartesian system button pressed;

export const EditModeNotationType = new Map<
  EditMode,
  NotationType
>([
  ["SPECIAL_SYMBOL_SELECTED", "SYMBOL"],
  ["EXPONENT_STARTED", "EXPONENT"],
  ["EXPONENT_WRITING", "EXPONENT"],
  ["TEXT_STARTED", "TEXT"],
  ["CELL_SELECTED", "SYMBOL"],
  ["POLYGON_STARTED", "POLYGON"],
  ["POLYGON_DRAWING", "POLYGON"],
  ["LINE_STARTED", "LINE"],
  ["LINE_DRAWING", "LINE"],
  ["LINE_SELECTED", "LINE"],
  ["LINE_EDITING_LEFT", "LINE"],
  ["LINE_EDITING_RIGHT", "LINE"],
  ["DIVISIONLINE_STARTED", "LINE"],
  ["DIVISIONLINE_DRAWING", "LINE"],
  ["DIVISIONLINE_SELECTED", "LINE"],
  ["DIVISIONLINE_EDITING_LEFT", "LINE"],
  ["DIVISIONLINE_EDITING_RIGHT", "LINE"],
  ["CURVE_STARTED", "CURVE"],
  ["CURVE_DRAWING", "CURVE"],
  ["CURVE_SELECTED", "CURVE"],
  ["CURVE_EDITING_LEFT", "CURVE"],
  ["CURVE_EDITING_RIGHT", "CURVE"],
  ["CURVE_EDITING_CONTROLֹ_POINT", "CURVE"],
  ["CIRCLE_STARTED", "CIRCLE"],
  ["CIRCLE_DRAWING", "CIRCLE"],
  ["CIRCLE_SELECTED", "CIRCLE"],
  ["CIRCLE_EDITING", "CIRCLE"],
  ["PARABOLA_STARTED", "CONIC"],
  ["HYPERBOLA_STARTED", "CONIC"],
  ["CONIC_DRAWING", "CONIC"],
  ["CONIC_SELECTED", "CONIC"],
  ["CONIC_EDITING_VERTEX", "CONIC"],
  ["CONIC_EDITING_SCALE", "CONIC"],
  ["CONIC_EDITING_OPENING", "CONIC"],
  ["CONIC_EDITING_THROUGH", "CONIC"],
  ["CURVE_EDITING_CONTROLֹ_POINT", "CURVE"],
  ["TEXT_WRITING", "TEXT"],
  ["TEXT_SELECTED", "TEXT"],
  ["IMAGE_SELECTED", "IMAGE"],
  ["IMAGE_CROPPING", "IMAGE"],
  ["ANNOTATION_STARTED", "ANNOTATION"],
  ["ANNOTATION_SELECTED", "ANNOTATION"],
  ["ANNOTATION_WRITING", "ANNOTATION"],
  ["TEXT_AREA_SELECTING", "TEXT"],
  ["TEXT_AREA_SELECTED", "TEXT"],
  ["SQRT_STARTED", "SQRT"],
  ["SQRT_DRAWING", "SQRT"],
  ["SQRT_SELECTED", "SQRT"],
  ["SQRT_EDITING", "SQRT"],
  ["FREE_SKETCH_STARTED", "FREESKETCH"],
  ["FREE_SKETCH_DRAWING", "FREESKETCH"],
  ["FREE_SKETCH_SELECTED", "FREESKETCH"],
  ["FREE_SKETCH_WITH_OCR_STARTED", "SYMBOL"],
  ["FREE_SKETCH_WITH_OCR_DRAWING", "SYMBOL"],
  ["COLORIZING", "SYMBOL"],
  ["DELETING", "SYMBOL"],
  ["AREA_SELECTION_STARTED", "SYMBOL"],
  ["AREA_SELECTED", "SYMBOL"],
  ["TEXT_AREA_SELECTING", "TEXT"],
  ["TEXT_AREA_SELECTED", "TEXT"],
  ["AREA_MOVING", "SYMBOL"],
  ["CHECKMARK_STARTED", "SYMBOL"],
  ["SEMICHECKMARK_STARTED", "SYMBOL"],
  ["XMARK_STARTED", "SYMBOL"],
]);

export const EditModeCursorType = new Map<
  EditMode,
  CursorType
>([
  ["SPECIAL_SYMBOL_SELECTED", "auto"],
  ["EXPONENT_STARTED", "auto"],
  ["EXPONENT_WRITING", "auto"],
  ["EXPONENT_SELECTED", "auto"],
  ["TEXT_STARTED", "text"],
  ["TEXT_WRITING", "text"],
  ["CELL_SELECTED", "auto"],
  ["POLYGON_STARTED", "auto"],
  ["LINE_STARTED", "auto"],
  ["LINE_DRAWING", "auto"],
  ["LINE_SELECTED", "auto"],
  ["LINE_EDITING_LEFT", "auto"],
  ["LINE_EDITING_RIGHT", "auto"],
  ["DIVISIONLINE_STARTED", "auto"],
  ["DIVISIONLINE_DRAWING", "auto"],
  ["DIVISIONLINE_SELECTED", "auto"],
  ["DIVISIONLINE_EDITING_LEFT", "auto"],
  ["DIVISIONLINE_EDITING_RIGHT", "auto"],
  ["CURVE_STARTED", "auto"],
  ["CURVE_DRAWING", "auto"],
  ["CIRCLE_STARTED", "auto"],
  ["CIRCLE_DRAWING", "auto"],
  ["CIRCLE_SELECTED", "auto"],
  ["CIRCLE_EDITING", "auto"],
  ["PARABOLA_STARTED", "crosshair"],
  ["HYPERBOLA_STARTED", "crosshair"],
  ["CONIC_DRAWING", "crosshair"],
  ["CONIC_SELECTED", "move"],
  ["CONIC_EDITING_VERTEX", "move"],
  ["CONIC_EDITING_SCALE", "nwse-resize"],
  ["CONIC_EDITING_OPENING", "nesw-resize"],
  ["CONIC_EDITING_THROUGH", "grab"],
  ["SQRT_STARTED", "auto"],
  ["SQRT_DRAWING", "auto"],
  ["SQRT_SELECTED", "auto"],
  ["SQRT_EDITING", "auto"],
  ["FREE_SKETCH_STARTED", "auto"],
  ["FREE_SKETCH_DRAWING", "auto"],
  ["FREE_SKETCH_SELECTED", "auto"],
  ["FREE_SKETCH_WITH_OCR_STARTED", "crosshair"],
  ["FREE_SKETCH_WITH_OCR_DRAWING", "crosshair"],
  ["COLORIZING", "auto"],
  ["DELETING", "auto"],
  ["AREA_SELECTION_STARTED", "auto"],
  ["AREA_SELECTED", "auto"],
  ["RESIZE_STARTED", "grabbing"],
  ["AREA_MOVING", "auto"],
  ["TEXT_AREA_SELECTING", "auto"],
  ["TEXT_AREA_SELECTED", "auto"],
  ["TEXT_SELECTED", "grab"],
  ["IMAGE_SELECTED", "grab"],
  ["IMAGE_CROPPING", "crosshair"],
  ["ANNOTATION_SELECTED", "grab"],
  ["CHECKMARK_STARTED", "auto"],
  ["SEMICHECKMARK_STARTED", "auto"],
  ["XMARK_STARTED", "auto"],
  ["CARTESIAN_SYSTEM_STARTED", "auto"],
  
]);

export type UserType =
  | "TEACHER"
  | "STUDENT"
  | "BOTH";

export type SelectionMoveDirection =
  | "LEFT"
  | "RIGHT"
  | "TOP"
  | "BOTTOM"
  | "LEFTTOP"
  | "LEFTBOTTOM"
  | "RIGHTTOP"
  | "RIGHTBOTTOM";

export type Color =
  | "none"
  | "gray"
  | "lightgray"
  | "darkgray"
  | "ciyan"
  | "blue"
  | "green"
  | "purple"
  | "orange"
  | "red"
  | "yellow"
  | "lightblue"
  | "lightgreen"
  | "pink"
  | "transparent";

export type LineHandleType =
  | "top"
  | "bottom"
  | "right"
  | "left";

type TextSyncUpdateData = {
  text: string;
  x: number;
  y: number;
  width: number;
  height: number;
  uuid: string;
};
